import { BehaviorSubject } from 'rxjs';
import type { Disposable } from '../../common/interfaces/disposable.interface';
import type { JsonDataItem } from '../generic-types';

export type ProxyValidationFunction = (property: string, oldValue: unknown, newValue: unknown) => void;

export interface DataProxyChange {
  property: string;
  oldValue: unknown;
  newValue: unknown;
}

/**
 * Manages the data object proxy used to notify the dynamic form when a data value changes.
 */
export class DataProxyService implements Disposable {
  private _dataProxy?: JsonDataItem;
  private _dataChangeSubject = new BehaviorSubject<DataProxyChange | undefined>(undefined);
  private _proxyCreatedSubject = new BehaviorSubject<JsonDataItem | undefined>(undefined);

  /**
   * Returns the current data proxy object.
   * @returns {JsonDataItem | undefined} The data proxy object.
   */
  public get dataProxy(): JsonDataItem | undefined {
    return this._dataProxy;
  }

  /**
   * Determines if the data proxy is created.
   * @returns {boolean} Returns true is the data proxy is defined.
   */
  public get hasDataProxy(): boolean {
    return this._dataProxy !== undefined;
  }

  public onDataChange() {
    return this._dataChangeSubject.asObservable();
  }

  public onProxyCreated() {
    return this._proxyCreatedSubject.asObservable();
  }

  public getRawData() {
    return {
      ...this._dataProxy,
    };
  }

  /**
   * Wraps the passed data with a Proxy object; this will be used to communicate data changes to the library.
   * @param {JsonDataItem} data - The data to proxy changes for.
   */
  public createDataProxy(data: JsonDataItem) {
    const changeSubject = this._dataChangeSubject;
    this._dataProxy = new Proxy(data, {
      /**
       * The set method is called when a property in the data object changes.
       * @param {JsonDataItem} target - The object that is changing
       * @param {string | symbol} property - The property that triggered the change.
       * @param value - The new value for the property.
       * @returns {boolean} True indicates the change is allowed, false means it is not allowed.
       */
      set(target: JsonDataItem, property: string | symbol, value: unknown): boolean {
        if (typeof property === 'string') {
          changeSubject.next({
            property,
            oldValue: target[property],
            newValue: value,
          });
          target[property] = value;
          return true;
        }

        return false;
      },
    });

    this._proxyCreatedSubject.next(this._dataProxy);
  }

  public dispose() {
    if (!this._dataChangeSubject.closed) {
      this._dataChangeSubject.complete();
    }

    if (!this._proxyCreatedSubject.closed) {
      this._proxyCreatedSubject.complete();
    }
  }
}
